import { ExperimentBase, ExperimentConfig } from "./experiment-base";
import { LLMWrapper } from "./llm-wrapper";

interface GameObject {
  x: number;
  y: number;
  width: number;
  height: number;
  velocityY: number;
}

interface Ball extends GameObject {
  velocityX: number;
  speed: number;
}

export class PongApp extends ExperimentBase {
  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private llm: LLMWrapper | null = null;

  private playerPaddle!: GameObject;
  private aiPaddle!: GameObject;
  private ball!: Ball;

  private isDragging = false;
  private dragOffset = 0;
  private animationId: number | null = null;
  private aiResponse: "up" | "down" | null = null;
  private isQuerying = false;
  private aiTargetY: number | null = null;
  private aiVisionCanvas: HTMLCanvasElement | null = null;
  private aiVisionCtx: CanvasRenderingContext2D | null = null;
  private prevBallVelocityX = 0;
  private readonly AI_VISION_W = 128;
  private readonly AI_VISION_H = 64;

  private readonly PADDLE_SPEED = 3;
  private readonly BALL_BASE_SPEED = 1;
  private readonly PADDLE_WIDTH = 15;
  private readonly PADDLE_HEIGHT = 80;
  private readonly BALL_SIZE = 10;

  constructor() {
    const config: ExperimentConfig = {
      id: "pong",
      title: "MURD Pong",
      tagline: "AI-Powered Pong",
    };
    super(config);
  }

  async init(llm: LLMWrapper): Promise<void> {
    this.llm = llm;
  }

  protected setupContent(): void {
    // Setup is done in activate
  }

  activate(): void {
    if (!this.contentElement) return;
    this.contentElement.innerHTML = `
      <div class="pong-container">
        <div class="pong-header">
          <h1>MURD PONG</h1>
          <p>Drag your paddle. Click to launch ball.</p>
        </div>
        <canvas id="pong-canvas"></canvas>
        <div class="pong-status" id="pong-status"></div>
        <div style="margin-top: 12px; display: flex; gap: 12px; align-items: center;">
          <span style="font-size: 12px; opacity: 0.7;">AI sees:</span>
          <canvas id="pong-ai-vision-mirror"
                  style="image-rendering: pixelated; width: 256px; height: 128px; border: 1px solid #444;"></canvas>
        </div>
      </div>
    `;

    this.canvas = document.getElementById("pong-canvas") as HTMLCanvasElement;
    this.ctx = this.canvas.getContext("2d")!;

    this.setupCanvas();
    this.setupAiVisionCanvas();
    this.initializeGameObjects();
    this.setupEventListeners();
    this.startGameLoop();
  }

  private setupAiVisionCanvas(): void {
    this.aiVisionCanvas = document.createElement("canvas");
    this.aiVisionCanvas.width = this.AI_VISION_W;
    this.aiVisionCanvas.height = this.AI_VISION_H;
    this.aiVisionCtx = this.aiVisionCanvas.getContext("2d", {
      willReadFrequently: true,
    });
  }

  private mirrorAiVisionToUI(): void {
    if (!this.aiVisionCanvas) return;
    const mirror = document.getElementById(
      "pong-ai-vision-mirror",
    ) as HTMLCanvasElement | null;
    if (!mirror) return;
    if (mirror.width !== this.AI_VISION_W || mirror.height !== this.AI_VISION_H) {
      mirror.width = this.AI_VISION_W;
      mirror.height = this.AI_VISION_H;
    }
    const mctx = mirror.getContext("2d");
    if (!mctx) return;
    mctx.imageSmoothingEnabled = false;
    mctx.clearRect(0, 0, mirror.width, mirror.height);
    mctx.drawImage(this.aiVisionCanvas, 0, 0);
  }

  private renderAiVision(): void {
    if (!this.aiVisionCtx || !this.aiVisionCanvas) return;
    const W = this.AI_VISION_W;
    const H = this.AI_VISION_H;
    const ctx = this.aiVisionCtx;

    // Disable AA — pixel-art rendering is sharper for the model.
    ctx.imageSmoothingEnabled = false;

    const sx = W / this.canvas.width;
    const sy = H / this.canvas.height;
    const r = Math.round;

    // Black background + gray border.
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "#777777";
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, W - 1, H - 1);
    // Faint center line, dimmer than border so it doesn't compete.
    ctx.strokeStyle = "#3a3a3a";
    ctx.beginPath();
    ctx.moveTo(W / 2, 1);
    ctx.lineTo(W / 2, H - 1);
    ctx.stroke();

    // Zone reference lines: 8 horizontal dividers carving the field into 9 zones,
    // so the model can directly count rather than estimate vertical position.
    ctx.strokeStyle = "#2c2c2c";
    ctx.lineWidth = 1;
    for (let i = 1; i <= 8; i++) {
      const y = (H * i) / 9;
      ctx.beginPath();
      ctx.moveTo(1, y);
      ctx.lineTo(W - 1, y);
      ctx.stroke();
    }

    // Zone number fiducials on BOTH left and right edges so the model can
    // directly read which zone any object is in.
    ctx.fillStyle = "#aaaaaa";
    ctx.font = "7px monospace";
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    for (let i = 1; i <= 9; i++) {
      const y = ((i - 0.5) / 9) * H;
      ctx.fillText(String(i), 2, y);
    }
    ctx.textAlign = "right";
    for (let i = 1; i <= 9; i++) {
      const y = ((i - 0.5) / 9) * H;
      ctx.fillText(String(i), W - 2, y);
    }
    ctx.textAlign = "left";

    // Paddles intentionally NOT drawn — the model only needs to predict where
    // the ball will cross the right edge based on its position and velocity.

    // Ball: filled circle, sized so it stays clearly distinct from paddles.
    const cx = this.ball.x * sx + (this.ball.width * sx) / 2;
    const cy = this.ball.y * sy + (this.ball.height * sy) / 2;
    const ballRadius = Math.max(2, Math.min(W, H) / 16);
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(cx, cy, ballRadius, 0, Math.PI * 2);
    ctx.fill();

    // Velocity vector from ball center: direction = where ball is heading,
    // length = how fast (proportional to current ball.speed).
    const mag = Math.hypot(this.ball.velocityX, this.ball.velocityY);
    if (mag > 0.001) {
      const ax = this.ball.velocityX / mag;
      const ay = this.ball.velocityY / mag;
      // Speed mapping: BASE_SPEED → ~25% of canvas; scales linearly with speed,
      // capped at 70% so the arrow never escapes the field.
      const minLen = Math.min(W, H) * 0.2;
      const maxLen = Math.min(W, H) * 0.7;
      const speedScale =
        (Math.min(W, H) * 0.25 * this.ball.speed) / this.BALL_BASE_SPEED;
      const len = Math.max(minLen, Math.min(maxLen, speedScale));
      const ex = cx + ax * len;
      const ey = cy + ay * len;
      ctx.strokeStyle = "#ffff00";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(ex, ey);
      ctx.stroke();
      // Arrow head — bigger and bolder so direction is unmistakable.
      const headLen = 5;
      const angle = Math.atan2(ay, ax);
      ctx.beginPath();
      ctx.moveTo(ex, ey);
      ctx.lineTo(
        ex - headLen * Math.cos(angle - Math.PI / 6),
        ey - headLen * Math.sin(angle - Math.PI / 6),
      );
      ctx.moveTo(ex, ey);
      ctx.lineTo(
        ex - headLen * Math.cos(angle + Math.PI / 6),
        ey - headLen * Math.sin(angle + Math.PI / 6),
      );
      ctx.stroke();
    }
  }

  private setupCanvas(): void {
    const container = this.canvas.parentElement!;
    const rect = container.getBoundingClientRect();
    this.canvas.width = Math.min(800, rect.width - 40);
    this.canvas.height = 400;
  }

  private initializeGameObjects(): void {
    const centerY = this.canvas.height / 2;

    this.playerPaddle = {
      x: 30,
      y: centerY - this.PADDLE_HEIGHT / 2,
      width: this.PADDLE_WIDTH,
      height: this.PADDLE_HEIGHT,
      velocityY: 0,
    };

    this.aiPaddle = {
      x: this.canvas.width - 30 - this.PADDLE_WIDTH,
      y: centerY - this.PADDLE_HEIGHT / 2,
      width: this.PADDLE_WIDTH,
      height: this.PADDLE_HEIGHT,
      velocityY: 0,
    };

    this.resetBall();
  }

  private resetBall(): void {
    this.ball = {
      x: this.playerPaddle.x + this.PADDLE_WIDTH + 20,
      y: this.playerPaddle.y + this.PADDLE_HEIGHT / 2,
      width: this.BALL_SIZE,
      height: this.BALL_SIZE,
      velocityX: 0,
      velocityY: 0,
      speed: this.BALL_BASE_SPEED, // Reset speed to base when ball is reset
    };
    // Reset AI to center position
    this.aiTargetY = this.canvas.height / 2 - this.PADDLE_HEIGHT / 2;
  }

  private setupEventListeners(): void {
    this.canvas.addEventListener("mousedown", this.handleMouseDown.bind(this));
    this.canvas.addEventListener("mousemove", this.handleMouseMove.bind(this));
    this.canvas.addEventListener("mouseup", this.handleMouseUp.bind(this));
    this.canvas.addEventListener("mouseleave", this.handleMouseUp.bind(this));

    this.canvas.addEventListener(
      "touchstart",
      this.handleTouchStart.bind(this),
      { passive: false },
    );
    this.canvas.addEventListener("touchmove", this.handleTouchMove.bind(this), {
      passive: false,
    });
    this.canvas.addEventListener("touchend", this.handleTouchEnd.bind(this));

    this.canvas.addEventListener("click", this.handleClick.bind(this));
  }

  private handleMouseDown(e: MouseEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    const mouseY = e.clientY - rect.top;
    const mouseX = e.clientX - rect.left;

    // Check if clicking on player's half of the field
    if (mouseX < this.canvas.width / 2) {
      this.isDragging = true;
      // For desktop, maintain relative offset for smoother control
      this.dragOffset = mouseY - this.playerPaddle.y;
    }
  }

  private handleMouseMove(e: MouseEvent): void {
    if (!this.isDragging) return;

    const rect = this.canvas.getBoundingClientRect();
    const mouseY = e.clientY - rect.top;
    const newY = mouseY - this.dragOffset;

    const prevY = this.playerPaddle.y;
    this.playerPaddle.y = Math.max(
      0,
      Math.min(newY, this.canvas.height - this.PADDLE_HEIGHT),
    );
    this.playerPaddle.velocityY = this.playerPaddle.y - prevY;
  }

  private handleMouseUp(): void {
    this.isDragging = false;
    this.playerPaddle.velocityY = 0;
  }

  private handleTouchStart(e: TouchEvent): void {
    e.preventDefault();
    const rect = this.canvas.getBoundingClientRect();
    const touch = e.touches[0];
    const touchY = touch.clientY - rect.top;
    const touchX = touch.clientX - rect.left;

    // Check if we should launch the ball
    if (this.ball.velocityX === 0 && this.ball.velocityY === 0) {
      this.launchBall();
      return;
    }

    // On mobile, if touching the player's half of the field, control the paddle
    if (touchX < this.canvas.width / 2) {
      this.isDragging = true;
      // Set the offset so the paddle moves to where they touched
      this.dragOffset = this.PADDLE_HEIGHT / 2; // Center the paddle on touch position
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    e.preventDefault();
    if (!this.isDragging) return;

    const rect = this.canvas.getBoundingClientRect();
    const touch = e.touches[0];
    const touchY = touch.clientY - rect.top;
    const newY = touchY - this.dragOffset;

    const prevY = this.playerPaddle.y;
    this.playerPaddle.y = Math.max(
      0,
      Math.min(newY, this.canvas.height - this.PADDLE_HEIGHT),
    );
    this.playerPaddle.velocityY = this.playerPaddle.y - prevY;
  }

  private handleTouchEnd(): void {
    this.isDragging = false;
    this.playerPaddle.velocityY = 0;
  }

  private handleClick(): void {
    if (this.ball.velocityX === 0 && this.ball.velocityY === 0) {
      this.launchBall();
    }
  }

  private launchBall(): void {
    const angle = ((Math.random() - 0.5) * Math.PI) / 4;
    this.ball.velocityX = this.ball.speed * Math.cos(angle);
    this.ball.velocityY = this.ball.speed * Math.sin(angle);
  }

  private isPointInPaddle(x: number, y: number, paddle: GameObject): boolean {
    return (
      x >= paddle.x &&
      x <= paddle.x + paddle.width &&
      y >= paddle.y &&
      y <= paddle.y + paddle.height
    );
  }

  private startGameLoop(): void {
    const loop = () => {
      this.update();
      this.render();
      this.animationId = requestAnimationFrame(loop);
    };
    loop();
  }

  private update(): void {
    if (this.ball.velocityX === 0 && this.ball.velocityY === 0) {
      this.ball.y =
        this.playerPaddle.y + this.PADDLE_HEIGHT / 2 - this.BALL_SIZE / 2;
      return;
    }

    this.ball.x += this.ball.velocityX;
    this.ball.y += this.ball.velocityY;

    if (
      this.ball.y <= 0 ||
      this.ball.y + this.ball.height >= this.canvas.height
    ) {
      this.ball.velocityY = -this.ball.velocityY;
    }

    if (this.checkPaddleCollision(this.ball, this.playerPaddle)) {
      this.handlePaddleHit(this.playerPaddle, true);
    }

    if (this.checkPaddleCollision(this.ball, this.aiPaddle)) {
      this.handlePaddleHit(this.aiPaddle, false);
    }

    if (this.ball.x < 0 || this.ball.x > this.canvas.width) {
      this.resetBall();
    }

    this.updateAiPaddle();

    // Keep querying the LLM as long as the ball is heading toward the AI paddle
    // and the runtime is free. Each response nudges aiTargetY up or down.
    this.prevBallVelocityX = this.ball.velocityX;
    if (this.ball.velocityX > 0 && !this.isQuerying && !this.llm?.isBusy()) {
      this.queryAi();
    }
  }

  private checkPaddleCollision(ball: Ball, paddle: GameObject): boolean {
    return (
      ball.x < paddle.x + paddle.width &&
      ball.x + ball.width > paddle.x &&
      ball.y < paddle.y + paddle.height &&
      ball.y + ball.height > paddle.y
    );
  }

  private handlePaddleHit(paddle: GameObject, isPlayer: boolean): void {
    const paddleCenter = paddle.y + paddle.height / 2;
    const ballCenter = this.ball.y + this.ball.height / 2;
    const relativeIntersectY = ballCenter - paddleCenter;
    const normalizedIntersect = relativeIntersectY / (paddle.height / 2);

    let bounceAngle = (normalizedIntersect * Math.PI) / 3;

    if (Math.abs(paddle.velocityY) > 1) {
      bounceAngle += ((paddle.velocityY / 10) * Math.PI) / 6;
    }

    // Increase speed by 0.5 after each collision
    const speed = this.ball.speed + 0.5;
    this.ball.speed = speed;

    if (isPlayer) {
      this.ball.velocityX = speed * Math.cos(bounceAngle);
    } else {
      this.ball.velocityX = -speed * Math.cos(bounceAngle);
    }

    this.ball.velocityY = speed * Math.sin(bounceAngle);

    if (isPlayer) {
      this.ball.x = paddle.x + paddle.width + 1;
    } else {
      this.ball.x = paddle.x - this.ball.width - 1;
    }
  }

  private updateAiPaddle(): void {
    if (this.aiTargetY === null) return;

    const prevY = this.aiPaddle.y;
    const diff = this.aiTargetY - this.aiPaddle.y;

    // Smooth movement toward target
    if (Math.abs(diff) > 2) {
      // Move at a fraction of the distance for smooth animation
      const moveAmount = diff * 0.1; // Adjust this value for smoother/faster movement
      this.aiPaddle.y += moveAmount;

      // Clamp to bounds
      this.aiPaddle.y = Math.max(
        0,
        Math.min(this.aiPaddle.y, this.canvas.height - this.PADDLE_HEIGHT),
      );
      this.aiPaddle.velocityY = this.aiPaddle.y - prevY;
    } else {
      // Close enough, snap to target
      this.aiPaddle.y = this.aiTargetY;
      this.aiPaddle.velocityY = 0;
    }
  }

  private async queryAi(): Promise<void> {
    if (!this.llm || this.isQuerying) return;
    this.isQuerying = true;

    // No physics shortcut — the paddle moves only on the LLM's say-so.
    const status = document.getElementById("pong-status");

    try {
      let response: string;
      let mode: "vision" | "text";

      if (this.llm.supportsVision() && this.aiVisionCanvas) {
        this.renderAiVision();
        this.mirrorAiVisionToUI();
        const visionPrompt =
          "This image shows a ball (white circle) and its velocity (yellow arrow).\n" +
          "The field is divided into 9 horizontal zones, numbered 1 (top) to 9 (bottom). The numbers are printed on both left and right edges.\n\n" +
          "Trace the yellow arrow forward to where it crosses the right edge. Read the zone number printed nearest that point.\n\n" +
          "Reply with EXACTLY one digit from 1 to 9.\n\n" +
          "Answer:";
        const t0 = performance.now();
        console.log("[pong] vision query start");
        response = await this.llm.askWithImage(
          visionPrompt,
          this.aiVisionCanvas,
          6,
          ["1", "2", "3", "4", "5", "6", "7", "8", "9"],
        );
        console.log(
          `[pong] vision query done in ${(performance.now() - t0).toFixed(0)}ms: "${response}"`,
        );
        mode = "vision";
      } else {
        // Mobile path: a 270M LLM can't reliably make even a binary UP/DOWN
        // decision, so resolve the paddle movement in JS. The status still
        // displays as "text" so the UI is unchanged.
        const ballCenterY = this.ball.y + this.ball.height / 2;
        const paddleCenterY = this.aiPaddle.y + this.PADDLE_HEIGHT / 2;
        const dy = ballCenterY - paddleCenterY;
        response =
          Math.abs(dy) < this.PADDLE_HEIGHT / 4
            ? "STAY"
            : dy < 0
              ? "UP"
              : "DOWN";
        mode = "text";
      }

      // Parse a single zone digit (1-9) from the vision path; UP/DOWN/STAY
      // keywords from the text-only fallback.
      let label = "?";
      let zone: number | null = null;
      const upperResp = response.toUpperCase();
      // Only the vision path uses digit→zone targeting; text path is UP/DOWN/STAY.
      if (mode === "vision") {
        const digitMatch = response.match(/[1-9]/);
        if (digitMatch) {
          zone = parseInt(digitMatch[0], 10);
          const actualPaddleZone = Math.max(
            1,
            Math.min(
              9,
              1 +
                Math.floor(
                  ((this.aiPaddle.y + this.PADDLE_HEIGHT / 2) /
                    this.canvas.height) *
                    9,
                ),
            ),
          );
          label = `ball=${zone} (paddle in ${actualPaddleZone})`;
        }
      }

      if (zone !== null) {
        // Map zone (1=top, 9=bottom) to the center Y of that zone, then
        // position the paddle so its center is on the ball's predicted Y.
        const zoneCenter = ((zone - 0.5) / 9) * this.canvas.height;
        this.aiTargetY = Math.max(
          0,
          Math.min(
            zoneCenter - this.PADDLE_HEIGHT / 2,
            this.canvas.height - this.PADDLE_HEIGHT,
          ),
        );
        // Coarse direction for the existing aiResponse field.
        const paddleCenter = this.aiPaddle.y + this.PADDLE_HEIGHT / 2;
        this.aiResponse =
          zoneCenter < paddleCenter - 5
            ? "up"
            : zoneCenter > paddleCenter + 5
              ? "down"
              : null;
      } else {
        // Text-fallback path: keyword direction with default magnitude of 2 paddles.
        let value = 0;
        if (/STAY/.test(upperResp)) {
          value = 0;
          label = "STAY";
        } else if (/\bUP\b/.test(upperResp)) {
          value = 2;
          label = "UP";
        } else if (/\bDOWN\b/.test(upperResp)) {
          value = -2;
          label = "DOWN";
        }
        this.aiResponse = value > 0 ? "up" : value < 0 ? "down" : null;
        const currentTarget = this.aiTargetY ?? this.aiPaddle.y;
        const nudge = -value * this.PADDLE_HEIGHT * 0.5;
        this.aiTargetY = Math.max(
          0,
          Math.min(
            currentTarget + nudge,
            this.canvas.height - this.PADDLE_HEIGHT,
          ),
        );
      }

      if (status) {
        const visionDis = this.llm.isVisionDisabled()
          ? " (vision disabled)"
          : "";
        status.textContent = `AI (${mode}): ${label}${visionDis}`;
      }
    } catch (error) {
      console.error("AI query failed:", error);
      if (status) {
        status.textContent = `AI: error — ${(error as Error).message.slice(0, 60)}`;
      }
    } finally {
      this.isQuerying = false;
    }
  }

  private render(): void {
    this.ctx.fillStyle = "#000000";
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.ctx.strokeStyle = "#4ac0c2";
    this.ctx.lineWidth = 2;
    this.ctx.setLineDash([10, 10]);
    this.ctx.beginPath();
    this.ctx.moveTo(this.canvas.width / 2, 0);
    this.ctx.lineTo(this.canvas.width / 2, this.canvas.height);
    this.ctx.stroke();
    this.ctx.setLineDash([]);

    this.ctx.fillStyle = "#4ac0c2";
    this.ctx.fillRect(
      this.playerPaddle.x,
      this.playerPaddle.y,
      this.playerPaddle.width,
      this.playerPaddle.height,
    );

    this.ctx.fillStyle = "#6fcf3f";
    this.ctx.fillRect(
      this.aiPaddle.x,
      this.aiPaddle.y,
      this.aiPaddle.width,
      this.aiPaddle.height,
    );

    this.ctx.fillStyle = "#ffffff";
    this.ctx.fillRect(
      this.ball.x,
      this.ball.y,
      this.ball.width,
      this.ball.height,
    );
  }

  deactivate(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }
}
